import React, { useEffect, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import CampoTexto from './CampoTexto';
import CampoDescripcionIA from './CampoDescripcionIA';
import CalculadoraPrecio from './CalculadoraPrecio';
import EtiquetaModal from './EtiquetaModal';
import MejorarConIAModal from './MejorarConIAModal';
import PublicarRedesModal from './PublicarRedesModal';
import CompartirProductoModal from './CompartirProductoModal';
import InterruptorCatalogo from './InterruptorCatalogo';
import { obtenerCategoria } from '../utils/categorias';
import { optimizarFoto } from '../utils/imagen';
import { useCategorias } from '../context/CategoriasContext';
import { useProductos } from '../hooks/useProductos';
import { valoresDeDimension } from '../utils/agrupar';
import { registrarVenta } from '../services/ventasService';
import { obtenerClientes } from '../services/clientesService';
import { obtenerCuentas, iconoTipo } from '../services/cuentasService';
import { validarProducto, normalizarProducto } from '../utils/validaciones';
import { fechaLocal, horaLocal, timestampParaFecha } from '../utils/fecha';
import { subirFotos } from '../services/fotosService';
import { actualizarProducto, eliminarProducto, marcarFotoPrincipal } from '../services/productosService';
import { mensajeAmigable } from '../utils/errores';
import { aNumero, numeroSeguro } from '../utils/validacion';
import CampoNumero from './CampoNumero';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import SelectorUnidad from './SelectorUnidad';
import SelectorImpuesto from './SelectorImpuesto';
import NuevaCategoriaProductoInline from './NuevaCategoriaProductoInline';
import { decimalesCantidad, esPorPeso, formatoCantidad, redondearCantidad, textosCampoStock } from '../utils/unidades';

const CAMPOS_EDICION = [
  { label: 'Nombre', campo: 'nombre', tipo: 'text' },
  { label: 'Marca', campo: 'marca', tipo: 'text' },
  { label: 'Talla', campo: 'talla', tipo: 'text' },
  { label: 'Color', campo: 'color', tipo: 'text' },
  { label: 'Stock', campo: 'stock', tipo: 'number' },
  { label: 'Precio costo (L)', campo: 'precio_costo', tipo: 'number' },
  { label: 'Precio venta (L)', campo: 'precio_venta', tipo: 'number' },
];

export default function ProductoDetalleModal({ producto: productoInicial, onCerrar, onActualizado }) {
  // Vendedor/a: puede vender e imprimir etiqueta, no editar, borrar ni usar IA.
  const { puede } = useAuth();
  const esVendedora = !puede('productos'); // sin permiso: no edita ni publica
  const { categorias } = useCategorias();
  const { productos: todosProductos } = useProductos();
  const [producto, setProducto] = useState(productoInicial);
  const [vendiendo, setVendiendo] = useState(false);
  const [panelVenta, setPanelVenta] = useState(false);
  const [cantidadVenta, setCantidadVenta] = useState('1');
  const [clienteVenta, setClienteVenta] = useState('');
  const [cuentaVenta, setCuentaVenta] = useState('');
  const [fechaVenta, setFechaVenta] = useState(fechaLocal());
  const [clientes, setClientes] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [mostrarEtiqueta, setMostrarEtiqueta] = useState(false);
  const [mostrarPublicar, setMostrarPublicar] = useState(false);
  const [mostrarCompartir, setMostrarCompartir] = useState(false);
  const [mostrarIA, setMostrarIA] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [edicionGuardada, setEdicionGuardada] = useState(false); // pantalla de éxito + etiqueta tras editar
  const [editando, setEditando] = useState(null);
  const [fotosNuevas, setFotosNuevas] = useState({ foto_1: null, foto_2: null, foto_3: null, foto_4: null, foto_5: null });
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [cambiandoCatalogo, setCambiandoCatalogo] = useState(false);
  const [creandoCategoria, setCreandoCategoria] = useState(false);
  const { mostrarToast } = useToast();
  const porPeso = esPorPeso(producto);
  // Por peso se puede vender menos de 1 (0.5 lb): "sin stock" es solo cero.
  const sinStock = porPeso ? !(producto.stock > 0) : producto.stock < 1;

  // Al abrir el panel de venta carga clientes y cuentas (para asociar la venta).
  useEffect(() => {
    if (!panelVenta) return;
    if (clientes.length === 0) obtenerClientes().then(setClientes).catch(() => {});
    if (cuentas.length === 0) {
      obtenerCuentas().then((cs) => {
        setCuentas(cs);
        if (cs.length > 0 && !cuentaVenta) setCuentaVenta(cs[0].id); // primera cuenta por defecto
      }).catch(() => {});
    }
  }, [panelVenta, clientes.length, cuentas.length, cuentaVenta]);

  const iniciarEdicion = () => {
    setEditando({ ...producto });
    setFotosNuevas({ foto_1: null, foto_2: null, foto_3: null, foto_4: null, foto_5: null });
    setErrores({});
    setModoEdicion(true);
  };

  const cancelarEdicion = () => {
    setModoEdicion(false);
    setEditando(null);
    setFotosNuevas({ foto_1: null, foto_2: null, foto_3: null, foto_4: null, foto_5: null });
    setErrores({});
  };

  const actualizarCampo = (campo, valor) => {
    setEditando((p) => ({ ...p, [campo]: valor }));
    setErrores((e) => ({ ...e, [campo]: undefined }));
  };

  const guardarEdicion = async () => {
    const nuevosErrores = validarProducto(editando);
    if (Object.keys(nuevosErrores).length > 0) {
      setErrores(nuevosErrores);
      return;
    }
    setErrores({});
    setGuardando(true);
    try {
      const datos = normalizarProducto(editando);
      const fotosUrls = await subirFotos(fotosNuevas);
      Object.assign(datos, fotosUrls);
      await actualizarProducto(producto.id, datos, producto);
      mostrarToast('¡Producto actualizado! ✅', 'exito');
      onActualizado(); // refresca la lista detrás
      // Reflejar los cambios (precio, nombre, talla) para la etiqueta y
      // mostrar la pantalla de éxito con opción de imprimir.
      setProducto((p) => ({ ...p, ...editando }));
      setModoEdicion(false);
      setEditando(null);
      setEdicionGuardada(true);
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  // 🛍️ Mostrar u ocultar del catálogo en línea con un toque (se guarda al instante).
  const cambiarCatalogo = async (mostrar) => {
    setCambiandoCatalogo(true);
    try {
      await actualizarProducto(producto.id, { activo: mostrar });
      setProducto((p) => ({ ...p, activo: mostrar }));
      onActualizado();
      mostrarToast(mostrar ? '🛍️ Listo: ya se ve en tu catálogo' : '🙈 Listo: ya no se ve en tu catálogo', 'exito');
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setCambiandoCatalogo(false);
    }
  };

  const confirmarVenta = async () => {
    const cantidad = aNumero(cantidadVenta);
    if (porPeso) {
      if (cantidad === null || cantidad <= 0) {
        mostrarToast(`Escribe cuántas ${producto.unidad === 'kg' ? 'kilos' : 'libras'} vas a vender, ej. 12.5`, 'error');
        return;
      }
    } else if (cantidad === null || !Number.isInteger(cantidad) || cantidad < 1) {
      mostrarToast('Escribe cuántas unidades vas a vender (número entero, mínimo 1)', 'error');
      return;
    }
    if (cantidad > producto.stock) {
      mostrarToast(`Solo hay ${formatoCantidad(producto.stock, producto)} en stock`, 'error');
      return;
    }
    setVendiendo(true);
    try {
      const nombreCuenta = cuentas.find((c) => c.id === cuentaVenta)?.nombre || null;
      const venta = await registrarVenta(producto.id, cantidad, null, {
        clienteId: clienteVenta || null,
        metodoPago: nombreCuenta,
        cuentaId: cuentaVenta || null,
        fecha: timestampParaFecha(fechaVenta),
        unidad: producto.unidad,
      });
      const restante = redondearCantidad(producto.stock - cantidad);
      mostrarToast(
        `Venta registrada: ${formatoCantidad(cantidad, producto)} × ${producto.codigo} = ${simboloMoneda()} ${venta.total} 💰 · Quedan ${formatoCantidad(restante, producto)} en stock`,
        'exito'
      );
      onActualizado();   // refresca la lista de inventario
      onCerrar();        // vuelve a la lista ya actualizada
    } catch (e) {
      const msg = (e?.message || '').includes('STOCK_INSUFICIENTE')
        ? 'No hay stock suficiente para esta venta.'
        : mensajeAmigable(e);
      mostrarToast(msg, 'error');
    } finally {
      setVendiendo(false);
    }
  };

  const confirmarEliminar = async () => {
    setEliminando(true);
    try {
      const { fotosLimpiadas } = await eliminarProducto(producto);
      mostrarToast(
        fotosLimpiadas
          ? `Producto ${producto.codigo} eliminado 🗑️`
          : `Producto ${producto.codigo} eliminado (las fotos no se pudieron limpiar)`,
        'exito'
      );
      onActualizado();
      onCerrar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
      setEliminando(false);
      setConfirmandoEliminar(false);
    }
  };

  const cambiarFotoPrincipal = async (numeroFoto) => {
    try {
      await marcarFotoPrincipal(producto.id, numeroFoto);
      setProducto((p) => ({ ...p, foto_principal: numeroFoto }));
      onActualizado();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    }
  };

  return (
    <div onClick={() => !modoEdicion && !guardando && onCerrar()} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '600px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h2 style={{ margin: 0, color: 'var(--color-primario,#DB2777)' }}>{modoEdicion ? '✏️ Editando' : producto.nombre}</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6B7280' }}>Código: {producto.codigo}</p>
          </div>
          <button onClick={() => (modoEdicion ? cancelarEdicion() : onCerrar())} disabled={guardando} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>✕</button>
        </div>

        {edicionGuardada && (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: '46px', lineHeight: 1 }}>✅</div>
            <h2 style={{ margin: '10px 0 4px', color: 'var(--k-verde)' }}>¡Cambios guardados!</h2>
            <p style={{ margin: '0 0 2px', fontSize: '15px', color: '#374151', fontWeight: 600 }}>{producto.nombre}</p>
            <p style={{ margin: '0 0 22px', fontSize: '13px', color: '#6B7280' }}>
              Código: <strong>{producto.codigo}</strong> · Precio: <strong>{simboloMoneda()} {producto.precio_venta}</strong>
            </p>
            <button onClick={() => setMostrarEtiqueta(true)}
              style={{ width: '100%', padding: '15px', backgroundColor: 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '10px' }}>
              🏷️ Imprimir etiqueta / código de barras
            </button>
            <button onClick={onCerrar}
              style={{ width: '100%', padding: '12px', backgroundColor: '#E5E7EB', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}>
              Listo
            </button>
          </div>
        )}

        {!modoEdicion && !edicionGuardada && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>📸 Fotos (clic para hacer principal)</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {[1, 2, 3, 4, 5].map((num) => {
                  const urlFoto = producto[`foto_${num}_url`];
                  const esPrincipal = producto.foto_principal === num;
                  return (
                    <div key={num} onClick={() => urlFoto && cambiarFotoPrincipal(num)}
                      style={{ position: 'relative', cursor: urlFoto ? 'pointer' : 'default', border: esPrincipal ? '3px solid var(--color-primario,#DB2777)' : '2px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', height: '140px', backgroundColor: '#F9FAFB' }}>
                      {urlFoto ? (
                        <img src={optimizarFoto(urlFoto, 700)} alt={`Foto ${num}`} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: 'white' }} />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF', fontSize: '12px' }}>Sin foto</div>
                      )}
                      {esPrincipal && (
                        <div style={{ position: 'absolute', top: '4px', right: '4px', backgroundColor: 'var(--color-primario,#DB2777)', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                          ⭐ Principal
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {puede('ia') && [1, 2, 3, 4, 5].some((n) => producto[`foto_${n}_url`]) && (
                <button onClick={() => setMostrarIA(true)}
                  style={{ width: '100%', marginTop: '10px', padding: '11px', borderRadius: '10px', border: '1px dashed var(--color-primario,#DB2777)', background: 'color-mix(in srgb, var(--color-primario,#DB2777) 6%, white)', color: 'var(--color-primario,#DB2777)', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
                  ✨ Mejorar con IA — crear foto profesional
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div><p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>Marca</p><p style={{ margin: 0, fontWeight: '600' }}>{producto.marca || '—'}</p></div>
              <div><p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>Categoría</p><p style={{ margin: 0, fontWeight: '600' }}>{obtenerCategoria(categorias, producto.categoria)?.icono} {producto.categoria}</p></div>
              <div><p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>Talla</p><p style={{ margin: 0, fontWeight: '600' }}>{producto.talla || '—'}</p></div>
              <div><p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>Color</p><p style={{ margin: 0, fontWeight: '600' }}>{producto.color || '—'}</p></div>
              <div><p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>Stock</p><p style={{ margin: 0, fontWeight: '600' }}>{formatoCantidad(producto.stock, producto)}</p></div>
              <div><p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>Precio venta{textosCampoStock(producto).porUnidad}</p><p style={{ margin: 0, fontWeight: '600', color: 'var(--color-primario,#DB2777)' }}>{simboloMoneda()} {producto.precio_venta}</p></div>
            </div>

            {producto.descripcion && (
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #F3F4F6' }}>
                <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#6B7280' }}>Descripción</p>
                <p style={{ margin: 0, fontSize: '13px', color: '#374151', lineHeight: 1.5 }}>{producto.descripcion}</p>
              </div>
            )}

            {!esVendedora && (
              <InterruptorCatalogo valor={producto.activo} onChange={cambiarCatalogo} deshabilitado={cambiandoCatalogo || eliminando} estilo={{ marginBottom: '12px' }} />
            )}

            {!panelVenta ? (
              <button onClick={() => setPanelVenta(true)} disabled={eliminando || sinStock}
                style={{ width: '100%', marginBottom: '8px', padding: '12px', backgroundColor: sinStock ? '#9CA3AF' : 'var(--k-verde)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 'bold', cursor: sinStock ? 'not-allowed' : 'pointer' }}>
                {sinStock ? 'Sin stock disponible' : '💰 Registrar Venta'}
              </button>
            ) : (
              <div style={{ marginBottom: '8px', padding: '12px', backgroundColor: '#D1FAE5', borderRadius: '8px', border: '1px solid #6EE7B7' }}>
                <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '600', color: '#065F46' }}>💰 Registrar venta</p>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ fontSize: '13px', color: '#065F46', fontWeight: 600 }}>{porPeso ? (producto.unidad === 'kg' ? 'Kilos:' : 'Libras:') : 'Cantidad:'}</label>
                  <CampoNumero valor={cantidadVenta} onChange={setCantidadVenta} deshabilitado={vendiendo}
                    decimales={decimalesCantidad(producto)} min={porPeso ? 0.01 : 1} minSiVacio={!porPeso} max={producto.stock}
                    maxMensaje={`Solo hay ${formatoCantidad(producto.stock, producto)} en existencia`}
                    estilo={{ width: porPeso ? '96px' : '80px', padding: '8px', border: '1px solid #6EE7B7', borderRadius: '6px', fontSize: '14px' }} />
                  <span style={{ fontSize: '13px', color: '#065F46' }}>
                    de {formatoCantidad(producto.stock, producto)} · Total: <strong>{simboloMoneda()} {((porPeso ? numeroSeguro(cantidadVenta) : Math.trunc(numeroSeguro(cantidadVenta))) * (producto.precio_venta || 0)).toFixed(2)}</strong>
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: '#065F46', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Cliente (opcional)</label>
                    <select value={clienteVenta} disabled={vendiendo} onChange={(e) => setClienteVenta(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #6EE7B7', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}>
                      <option value="">Venta rápida (sin cliente)</option>
                      {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#065F46', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Entra a la cuenta</label>
                    <select value={cuentaVenta} disabled={vendiendo} onChange={(e) => setCuentaVenta(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #6EE7B7', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}>
                      {cuentas.length === 0 && <option value="">Sin cuentas — crea una en 🏦 Cuentas</option>}
                      {cuentas.map((c) => <option key={c.id} value={c.id}>{iconoTipo(c.tipo)} {c.nombre}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '12px', color: '#065F46', fontWeight: 600, display: 'block', marginBottom: '2px' }}>🗓️ Fecha de la venta</label>
                  <input type="date" value={fechaVenta} max={fechaLocal()} disabled={vendiendo}
                    onChange={(e) => setFechaVenta(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #6EE7B7', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                  <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#047857' }}>
                    {fechaVenta === fechaLocal()
                      ? `Hoy · ${horaLocal()} (hora actual)`
                      : '📅 Venta de otro día · se guarda con la hora actual'}
                  </p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button onClick={() => { setPanelVenta(false); setCantidadVenta('1'); }} disabled={vendiendo}
                    style={{ padding: '10px', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                    Cancelar
                  </button>
                  <button onClick={confirmarVenta} disabled={vendiendo}
                    style={{ padding: '10px', backgroundColor: vendiendo ? '#9CA3AF' : 'var(--k-verde)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: vendiendo ? 'not-allowed' : 'pointer' }}>
                    {vendiendo ? '⏳ Registrando...' : 'Confirmar venta'}
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: esVendedora ? '1fr' : '1fr 1fr', gap: '8px' }}>
              {!esVendedora && <button onClick={iniciarEdicion} disabled={eliminando} style={{ padding: '12px', backgroundColor: 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}>
                ✏️ Editar
              </button>}
              <button onClick={() => setMostrarEtiqueta(true)} disabled={eliminando} style={{ padding: '12px', backgroundColor: 'white', color: 'var(--color-primario,#DB2777)', border: '2px solid var(--color-primario,#DB2777)', borderRadius: '8px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}>
                🏷️ Etiqueta
              </button>
            </div>
            <button onClick={() => setMostrarCompartir(true)} disabled={eliminando}
              style={{ width: '100%', marginTop: '8px', padding: '12px', backgroundColor: '#25D366', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}>
              📤 Compartir (WhatsApp, Instagram, Facebook)
            </button>

            {!esVendedora && <>
            <button onClick={() => setMostrarPublicar(true)} disabled={eliminando}
              style={{ width: '100%', marginTop: '8px', padding: '12px', backgroundColor: 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}>
              📣 Publicar en redes
            </button>

            {!puede('borrar') ? null : !confirmandoEliminar ? (
              <button onClick={() => setConfirmandoEliminar(true)} style={{ width: '100%', marginTop: '8px', padding: '10px', backgroundColor: 'white', color: '#DC2626', border: '1px solid #FCA5A5', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}>
                🗑️ Eliminar Producto
              </button>
            ) : (
              <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#FEE2E2', borderRadius: '8px', border: '1px solid #FCA5A5' }}>
                <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#991B1B', fontWeight: '600', textAlign: 'center' }}>
                  ¿Segura que quieres eliminar "{producto.nombre}" ({producto.codigo})? Esta acción no se puede deshacer.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button onClick={() => setConfirmandoEliminar(false)} disabled={eliminando} style={{ padding: '10px', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                    No, cancelar
                  </button>
                  <button onClick={confirmarEliminar} disabled={eliminando} style={{ padding: '10px', backgroundColor: eliminando ? '#9CA3AF' : '#DC2626', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: eliminando ? 'not-allowed' : 'pointer' }}>
                    {eliminando ? '⏳ Eliminando...' : 'Sí, eliminar'}
                  </button>
                </div>
              </div>
            )}
            </>}
          </>
        )}

        {modoEdicion && editando && (
          <>
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#FFF5F7', borderRadius: '8px', border: '1px solid #FBCFE8' }}>
              <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '600', color: 'var(--color-primario,#DB2777)' }}>📸 Fotos del producto</p>
              {[1, 2, 3, 4, 5].map((num) => {
                const urlExistente = editando[`foto_${num}_url`];
                return (
                  <div key={num} style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                      Foto {num} {urlExistente ? '(reemplazar)' : '(agregar)'}
                    </label>
                    <input type="file" accept="image/*" disabled={guardando}
                      onChange={(e) => setFotosNuevas((f) => ({ ...f, [`foto_${num}`]: e.target.files[0] }))}
                      style={{ width: '100%', padding: '6px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }} />
                    {urlExistente && !fotosNuevas[`foto_${num}`] && (
                      <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#10B981' }}>✓ Ya tiene foto</p>
                    )}
                  </div>
                );
              })}
            </div>

            <SelectorImpuesto valor={editando.impuesto} deshabilitado={guardando}
              onChange={(v) => actualizarCampo('impuesto', v)} />

            {CAMPOS_EDICION.map((c) => (
              <React.Fragment key={c.campo}>
                {c.campo === 'stock' && (
                  <SelectorUnidad valor={editando.unidad} deshabilitado={guardando}
                    onChange={(v) => { actualizarCampo('unidad', v); setErrores((e) => ({ ...e, stock: undefined })); }} />
                )}
                <CampoTexto label={c.campo === 'stock' ? textosCampoStock(editando).label
                    : (textosCampoStock(editando).porUnidad && c.label.endsWith(' (L)') ? c.label.replace(' (L)', `${textosCampoStock(editando).porUnidad} (L)`) : c.label)}
                  tipo={c.tipo} valor={editando[c.campo]}
                  error={errores[c.campo]} deshabilitado={guardando}
                  onChange={(v) => actualizarCampo(c.campo, v)} />
              </React.Fragment>
            ))}

            <CalculadoraPrecio
              costo={editando.precio_costo}
              precioVenta={editando.precio_venta}
              onAplicarPrecio={(v) => actualizarCampo('precio_venta', v)}
              deshabilitado={guardando}
            />

            <CampoDescripcionIA
              valor={editando.descripcion}
              onChange={(v) => actualizarCampo('descripcion', v)}
              datosProducto={editando}
              deshabilitado={guardando}
            />

            <InterruptorCatalogo valor={editando.activo} onChange={(v) => actualizarCampo('activo', v)} deshabilitado={guardando} estilo={{ marginBottom: '16px' }} />

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Categoría</label>
              <select value={editando.categoria} disabled={guardando}
                onChange={(e) => (e.target.value === '__nueva__' ? setCreandoCategoria(true) : actualizarCampo('categoria', e.target.value))}
                style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px' }}>
                {categorias.map((cat) => (
                  <option key={cat.nombre} value={cat.nombre}>{cat.icono} {cat.nombre}</option>
                ))}
                <option value="__nueva__">➕ Crear categoría nueva…</option>
              </select>
              {creandoCategoria && (
                <NuevaCategoriaProductoInline onCerrar={() => setCreandoCategoria(false)}
                  onCreada={(nombre) => { setCreandoCategoria(false); actualizarCampo('categoria', nombre); }} />
              )}
            </div>

            {editando.categoria && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                  Tipo <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(dentro de {editando.categoria})</span>
                </label>
                <input list="tipos-sugeridos-edicion" value={editando.subtipo || ''} disabled={guardando}
                  onChange={(e) => actualizarCampo('subtipo', e.target.value)}
                  placeholder="Ej. Brasieres, Panties, Fajas talladoras…"
                  style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                <datalist id="tipos-sugeridos-edicion">
                  {valoresDeDimension(todosProductos.filter((p) => p.categoria === editando.categoria), 'subtipo').valores.map((t) => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
              </div>
            )}

            {guardando && (
              <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: '#FEF3C7', borderRadius: '8px', border: '1px solid #FCD34D', textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#92400E', fontWeight: '600' }}>⏳ Guardando cambios, por favor espera...</p>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <button onClick={cancelarEdicion} disabled={guardando} style={{ padding: '12px', backgroundColor: '#E5E7EB', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: guardando ? 'not-allowed' : 'pointer', opacity: guardando ? 0.5 : 1 }}>
                Cancelar
              </button>
              <button onClick={guardarEdicion} disabled={guardando} style={{ padding: '12px', backgroundColor: guardando ? '#9CA3AF' : 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: guardando ? 'not-allowed' : 'pointer' }}>
                {guardando ? '⏳ Guardando...' : '💾 Guardar'}
              </button>
            </div>
          </>
        )}

        {mostrarEtiqueta && (
          <EtiquetaModal producto={producto} onCerrar={() => setMostrarEtiqueta(false)} />
        )}

        {mostrarCompartir && <CompartirProductoModal producto={producto} onCerrar={() => setMostrarCompartir(false)} />}
        {mostrarPublicar && (
          <PublicarRedesModal producto={producto} onCerrar={() => setMostrarPublicar(false)} />
        )}

        {mostrarIA && (
          <MejorarConIAModal
            producto={producto}
            onCerrar={() => setMostrarIA(false)}
            onActualizado={(cambios) => { if (cambios) setProducto((p) => ({ ...p, ...cambios })); onActualizado && onActualizado(); }}
          />
        )}
      </div>
    </div>
  );
}
